<template>
  <div>
    <!-- Hero Section with Vuetify components for better responsiveness -->
    <v-container fluid class="hero-background pa-0">
      <v-container class="py-16">
        <v-row justify="center">
          <v-col cols="12" md="10" lg="8" class="text-center">
            <h1 class="font-heading text-white mb-6" 
                :class="$vuetify.display.mobile ? 'text-h3' : 'text-h1'">
              Ordenamos su empresa para que disfrute de sus 
              <span class="text-brand-orange">beneficios.</span>
            </h1>
            <p class="text-h6 text-grey-lighten-2 mb-8">
              Con más de 20 años de trayectoria, nuestro equipo de profesionales 
              transforma desafíos en oportunidades de crecimiento sostenible.
            </p>
            <v-btn
              href="#servicios"
              color="orange"
              size="large"
              class="text-white font-weight-bold"
              elevation="4"
            >
              Conocer Nuestros Servicios
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-container>

    <!-- About Section with improved responsive grid -->
    <v-container id="nosotros" class="py-16">
      <v-row>
        <v-col cols="12" class="text-center mb-8">
          <h2 class="font-heading text-h3 mb-4">Quiénes Somos</h2>
          <v-divider class="mx-auto bg-brand-orange" style="max-width: 100px; height: 4px;"></v-divider>
        </v-col>
      </v-row>
      
      <v-row align="center">
        <v-col cols="12" md="8">
          <p class="text-body-1 text-grey-darken-2 mb-6">
            Estudio Gallo Puló es una organización con más de 20 años de trayectoria en el medio, 
            la cual está integrada por un equipo de profesionales cuya formación está enfocada en 
            la Gestión Integral de Empresas, asesorando en todas las áreas que la componen.
          </p>
          
          <v-card variant="tonal" class="p-6 rounded-lg">
            <template v-slot:text>
              <v-list density="compact">
                <v-list-subheader class="text-subtitle-1 font-weight-bold text-brand-brown mb-2">
                  Director: Cr. José Gallo Puló
                </v-list-subheader>
                <v-list-item class="px-0">
                    <template v-slot:prepend>
                      <v-icon color="orange">mdi-circle-medium</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2 text-wrap">
                      <strong>Contador Público Nacional</strong> – Universidad Nacional de Salta
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item class="px-0">
                    <template v-slot:prepend>
                      <v-icon color="orange">mdi-circle-medium</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2 text-wrap">
                      <strong>Posgrado en Administración de Negocios</strong> – Universidad de Belgrano
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item class="px-0">
                    <template v-slot:prepend>
                      <v-icon color="orange">mdi-circle-medium</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2 text-wrap">
                      <strong>Diplomatura en Gestión de Negocios y Empresas</strong> – Universidad de San Pablo T
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item class="px-0">
                    <template v-slot:prepend>
                      <v-icon color="orange">mdi-circle-medium</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2 text-wrap">
                      <strong>Diplomatura en Fideicomisos</strong> – Universidad Católica de Salta
                    </v-list-item-title>
                  </v-list-item>
              </v-list>
            </template>
          </v-card>
        </v-col>
        
        <v-col cols="12" md="4">
          <v-img
            src="/images/estgallopullo.jpg"
            alt="Equipo del Estudio Gallo Puló"
            class="rounded-lg"
            cover
            height="400"
          ></v-img>
        </v-col>
      </v-row>

      <v-row class="mt-12">
        <v-col cols="12">
          <v-card class="bg-dark-charcoal text-white pa-8" elevation="8">
            <v-card-title 
              class="font-heading text-h4 text-center mb-4"
              style="white-space: normal; overflow: visible; text-overflow: unset;"
            >
              Nuestra Misión
            </v-card-title>
            <v-divider class="mx-auto bg-brand-orange mb-8" style="max-width: 100px; height: 4px;"></v-divider>
            <v-card-text class="text-center text-body-1 text-grey-lighten-2">
              Impulsar el desarrollo sostenible de la comunidad empresaria y emprendedora, 
              brindando asesoramiento contable, fiscal, laboral y de gestión de negocios, 
              con cercanía, compromiso y visión estratégica. En Estudio Gallo Puló trabajamos 
              para que cada cliente —desde el emprendedor que da sus primeros pasos hasta la 
              PyME consolidada— cuente con un aliado confiable que entiende su realidad, 
              lo acompaña con respuestas concretas y lo ayuda a tomar mejores decisiones.
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Services Section with responsive card grid -->
    <v-container id="servicios" fluid class="bg-light-gray py-16">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center mb-8">
            <h2 class="font-heading text-h3 mb-4">
              Un socio estratégico para cada área de su negocio
            </h2>
            <v-divider class="mx-auto bg-brand-orange" style="max-width: 100px; height: 4px;"></v-divider>
          </v-col>
        </v-row>
        
        <v-row>
          <v-col
            v-for="service in services"
            :key="service.title"
            cols="12"
            sm="6"
            lg="4"
          >
            <v-card class="h-100 card-hover-effect" elevation="2">
              <v-card-text class="pa-6">
                <h3 class="font-heading text-h6 text-brand-brown mb-3">
                  {{ service.title }}
                </h3>
                <p class="text-body-2 text-grey-darken-1">
                  {{ service.description }}
                </p>
              </v-card-text>
              <v-card-actions class="d-flex justify-center">
                <v-btn prepend-icon="mdi-plus-circle" :to=service.route>
                  Ver Mas
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-container>

    <!-- CSR Section with responsive image -->
    <v-container id="rse" class="py-16">
      <v-row>
        <v-col cols="12" class="text-center">
          <h2 class="font-heading text-h3 mb-4">Responsa<wbr>bilidad Social Empresarial</h2>
          <v-divider class="mx-auto bg-brand-orange mb-6" style="max-width: 100px; height: 4px;"></v-divider>
          <p class="text-body-1 text-grey-darken-2 mb-8 mx-auto" style="max-width: 800px;">
            Como parte de nuestra responsabilidad social, ofrecemos asesoramiento gratuito 
            a nuevos emprendimientos locales, convencidos de que el crecimiento de cada 
            negocio fortalece a toda la comunidad.
          </p>
          <v-img
            src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=2670&auto=format&fit=crop"
            alt="Asesoramiento a emprendedores"
            class="rounded-lg mx-auto"
            max-width="900"
            cover
          ></v-img>
          <div class="mx-auto mt-3" style="max-width: 900px;">
            <v-btn
              color="orange"
              to="/consultoria"
              block
            >
              Contactanos
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- News Section with responsive card grid -->
    <v-container id="novedades" fluid class="bg-light-gray py-16">
      <v-container>
        <!-- Loader -->
        <v-row v-if="loading" justify="center" class="my-8">
          <v-progress-circular indeterminate color="orange" size="64" />
        </v-row>

        <!-- Error -->
        <v-row v-if="error" justify="center" class="my-8">
          <v-col cols="12" class="text-center text-red">
            <v-icon color="red" size="40">mdi-alert-circle</v-icon>
            <p class="mt-4">{{ error }}</p>
          </v-col>
        </v-row>
        
        <!-- Título y descripción -->
        <v-row>
          <v-col cols="12" class="text-center mb-8">
            <h2 class="font-heading text-h3 mb-4">Novedades</h2>
            <p
              class="text-body-1 text-grey-darken-2 mx-auto"
              style="max-width: 600px;"
            >
              Análisis de interés sobre el panorama empresarial actual, curados por nuestro equipo.
            </p>
          </v-col>
        </v-row>

        <!-- Noticias -->
        <v-row>
          <!--Si hay Noticias Disponibles-->
          <v-col cols="12" v-if="noticias && noticias.length">
            <v-row>
              <v-col
                v-for="noticia in noticias"
                :key="noticia.id_noticia"
                cols="12"
                sm="6"
                lg="4"
              >
                <v-card class="h-100 card-hover-effect" elevation="2">
                  <!-- Imagen opcional -->
                  <v-img
                    v-if="noticia.imagen_url || noticia.imagen"
                    :src="resolveNewsImageUrl(noticia)"
                    :alt="noticia.titulo"
                    height="200"
                    cover
                  />
                  <v-sheet
                    v-else
                    height="200"
                    class="d-flex align-center justify-center bg-grey-lighten-3"
                  >
                    <v-icon color="grey" size="48">mdi-image-off-outline</v-icon>
                  </v-sheet>

                  <v-card-text class="pa-6">
                    <v-chip size="small" color="orange" variant="outlined" class="mb-3">
                      {{ formatFecha(noticia.fecha_publicacion) }}
                    </v-chip>

                    <h3 class="font-heading text-h6 mb-4">
                      {{ noticia.titulo }}
                    </h3>

                    <v-btn
                      variant="text"
                      color="orange"
                      class="pa-0"
                      style="text-transform: none;"
                      @click="verDetalle(noticia.id_noticia)"
                    >
                      Leer más
                      <v-icon end>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
          <!--En caso de no haber Noticias Disponibles-->
          <v-col cols="12" v-else class="text-center py-10">
            📰 No hay noticias disponibles por el Momento
          </v-col>
        </v-row>
      </v-container>
    </v-container>

    <!-- Contact Section with better responsive design -->
    <v-container id="contacto" fluid class="bg-brand-brown py-16">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="8" class="text-center">
            <h2 class="font-heading text-h3 text-white mb-4">
              Iniciemos una conversación
            </h2>
            <p class="text-body-1 text-grey-lighten-2 mb-8">
              Estamos listos para entender sus desafíos y diseñar una solución a medida. 
              Contáctenos para coordinar una reunión.
            </p>
            <v-btn
              href="mailto:estudio@estudiogallopulo.com.ar"
              color="white"
              size="large"
              class="text-brand-brown font-weight-bold"
              elevation="4"
            >
              Enviar E-mail
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-container>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from "vue-router"
import { resolveNewsImageUrl } from "@/utils/resolveUploadUrl"

const store = useStore()
const router = useRouter()

const services = computed(() => store.getters.getServices)
const noticias = computed(() => store.getters["noticias/getAllNoticias"])
const loading = computed(() => store.getters["noticias/isLoading"])
const error = computed(() => store.getters["noticias/getError"])

const fetchNoticias = async () => {
  await store.dispatch("noticias/fetchLandingNoticias")
}

const verDetalle = (id) => {
  router.push(`/noticia/${id}`)
}

const formatFecha = (fecha) => {
  return new Date(fecha).toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  })
}

onMounted(fetchNoticias)
</script>

<style scoped>
.hero-background {
  background-image: linear-gradient(rgba(45, 45, 45, 0.5), rgba(45, 45, 45, 0.5)), 
                    url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2511&auto=format&fit=crop');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
}

@media (max-width: 768px) {
  .hero-background {
    background-attachment: scroll;
  }
}

.card-hover-effect {
  transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
}

.card-hover-effect:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.15) !important;
}

.text-red {
  color: #d32f2f;
}
</style>
